import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChatCircleDots, PaperPlaneTilt, Trash, CheckCircle, Pencil, MicrophoneStage, ArrowSquareOut, DownloadSimple,
} from '@phosphor-icons/react';
import AudioRecorder from './AudioRecorder';
import AiFeedbackButton from './AiFeedbackButton';
import { useAuth } from '../contexts/AuthContext';
import { fileUrl as buildFileUrl } from '../utils/fileUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (u) => buildFileUrl(u);

// Tiny inline "Note: X /10" input the teacher uses to score a student's
// Sprechen/Schreiben answer. Persists on blur (so a single keystroke doesn't
// fire a request) or on Enter. Backend endpoint: PATCH /api/teacher/comments/{id}/score
const ScoreCommentWidget = ({ commentId, initial = '', onSaved }) => {
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10) {
      toast.error('Note 1–10');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/teacher/comments/${commentId}/score`, { score: n }, { withCredentials: true });
      toast.success(`Note ${n}/10 gespeichert`);
      onSaved?.(n);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5" title="Schnellbewertung 1–10">
      <span className="text-[9px] uppercase font-bold tracking-wider text-amber-700">Note</span>
      <input
        type="number"
        min={1}
        max={10}
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={e => { const x = e.target.value; if (x && Number(x) !== Number(initial)) save(x); }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        className="w-9 text-center text-xs border-0 focus:outline-none bg-transparent font-bold text-amber-700"
        data-testid={`score-comment-${commentId}`}
        disabled={saving}
        placeholder="–"
      />
      <span className="text-[9px] text-stone-400">/10</span>
    </div>
  );
};

/**
 * Discussion + corrections thread for a single material.
 * - Any participant may post a text comment and/or an audio reply (the global
 *   AudioRecorder handles preview, pause/resume, delete and upload).
 * - Teachers replying to a student comment flag the parent as "corrected",
 *   shown with a green pill. Teacher corrections are highlighted with a
 *   distinct ribbon so the flow (original → correction) is obvious.
 * - **Grammatik special case** (category === 'grammar'): students don't reply
 *   with audio or files — only plain-text "dudas". Each root duda is mirrored
 *   into Fragen → Grammatik so the teacher answers in one place and the
 *   conversation stays visible here too. The composer placeholder changes
 *   accordingly and the audio recorder is hidden.
 */
export default function MaterialComments({ materialId, canParticipate = true, category = null }) {
  const { user, demoMode } = useAuth();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const isGrammar = category === 'grammar';
  // Sprechen students reply by voice only — text is for the teacher's
  // corrections. Hides the textarea and the "Enviar texto" button when the
  // current user is a student on a speaking material.
  const isSpeaking = category === 'speaking';
  // Teacher-only "Als Schüler ansehen" preview: when ON, the teacher sees the
  // composer with the student's rules (e.g. audio-only on Sprechen, no other
  // comments visible), so they can validate UX without logging in as student.
  // Default tracks the platform-wide demoMode.
  const [studentMode, setStudentMode] = useState(demoMode);
  useEffect(() => { setStudentMode(demoMode); }, [demoMode]);
  const effectiveIsStudent = !isTeacher || studentMode;
  const studentOnSpeaking = isSpeaking && effectiveIsStudent;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/materials/${materialId}/comments`, { withCredentials: true });
      setItems(res.data || []);
    } catch { /* hide errors (e.g. 403 for other-class students) */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [materialId]);

  const postComment = async ({ body, audio_url, parent_id }) => {
    try {
      await axios.post(`${API}/materials/${materialId}/comments`,
        { body, audio_url, parent_id },
        { withCredentials: true });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      throw e;
    }
  };

  const del = async (comment_id) => {
    if (!window.confirm('¿Borrar mensaje?')) return;
    try {
      await axios.delete(`${API}/materials/${materialId}/comments/${comment_id}`, { withCredentials: true });
      load();
    } catch { toast.error('Error'); }
  };

  // Group by parent_id → { null: [root], [root_id]: [replies] }
  const roots = items.filter(c => !c.parent_id);
  const repliesOf = (id) => items.filter(c => c.parent_id === id);

  const roleBadge = (c) =>
    c.author_role === 'admin' || c.author_role === 'teacher'
      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Profesor</span>
      : null;

  const renderComment = (c, opts = {}) => (
    <div
      key={c.comment_id}
      className={`rounded-xl p-3 ${opts.isCorrection
        ? 'bg-emerald-50/70 border border-emerald-300 ring-1 ring-emerald-200 shadow-sm'
        : 'bg-white border border-stone-200'}`}
      data-testid={`comment-${c.comment_id}`}
    >
      {opts.isCorrection && (
        <div className="flex items-center gap-1 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
          <CheckCircle size={12} weight="fill" /> Corrección del profesor
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-[#78716C]">
            <span className="font-semibold text-[#292524]">{c.author_name}</span> {roleBadge(c)}
            <span className="ml-1.5">· {new Date(c.created_at).toLocaleString()}</span>
            {c.corrected && !opts.isCorrection && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full" data-testid={`corrected-${c.comment_id}`}>
                <CheckCircle size={10} weight="fill" /> Corregido
              </span>
            )}
          </p>
          {c.body && <p className="text-sm text-[#292524] mt-1 whitespace-pre-wrap break-words">{c.body}</p>}
          {c.linked_question_id && (
            <Link
              to={`/dudas/${c.linked_question_id}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#AB1C24] hover:underline"
              data-testid={`linked-question-${c.comment_id}`}
            >
              <ArrowSquareOut size={11} weight="fill" />
              Ver en Fragen → Grammatik
            </Link>
          )}
          {c.audio_url && (
            <audio
              controls
              preload="metadata"
              src={fileUrl(c.audio_url)}
              className="mt-2 h-9 max-w-md w-full rounded-full"
              style={{ maxHeight: '36px' }}
              data-testid={`audio-${c.comment_id}`}
            />
          )}
          {/* KI-Feedback for the student's own audio answer or for any teacher */}
          {c.audio_url && (isTeacher || c.author_id === user?.user_id) && (
            <div className="mt-2">
              <AiFeedbackButton commentId={c.comment_id} cachedFeedback={c.ai_feedback} />
            </div>
          )}
        </div>
        {(isTeacher || c.author_id === user?.user_id) && (
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            {/* Score 1–10 widget for Sprechen / Schreiben answers from
                students. Backend persists onto material_comments.teacher_score
                and the per-class stats panel aggregates these into the
                "Sprechen" / "Schreiben" averages. */}
            {isTeacher
              && (category === 'speaking' || category === 'writing')
              && c.author_id !== user?.user_id
              && !c.is_correction && (
              <ScoreCommentWidget
                commentId={c.comment_id}
                initial={c.teacher_score || ''}
                onSaved={(n) => setItems(prev => prev.map(it => it.comment_id === c.comment_id ? { ...it, teacher_score: n } : it))}
              />
            )}
            <button onClick={() => del(c.comment_id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600" data-testid={`del-comment-${c.comment_id}`}>
              <Trash size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Replies */}
      {!opts.isReply && repliesOf(c.comment_id).length > 0 && (
        <div className="mt-2 ml-3 pl-3 border-l-2 border-emerald-300 space-y-2">
          {repliesOf(c.comment_id).map(r => renderComment(r, { isReply: true, isCorrection: r.is_correction }))}
        </div>
      )}

      {/* Teacher reply composer */}
      {!opts.isReply && isTeacher && c.author_id !== user?.user_id && (
        replyTo === c.comment_id ? (
          <div className="mt-2 bg-[#FAF9F6] rounded-lg p-2 space-y-2 border border-emerald-200">
            <textarea
              className="w-full border border-stone-300 rounded px-2 py-1 text-sm"
              rows={2}
              placeholder="Escribe la corrección…"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              data-testid={`reply-text-${c.comment_id}`}
            />
            <AudioRecorder
              testId={`reply-audio-${c.comment_id}`}
              compact
              maxSeconds={300}
              onUploaded={async (f) => {
                await postComment({ body: replyText, audio_url: f.url, parent_id: c.comment_id });
                setReplyTo(null); setReplyText('');
              }}
              disabled={isGrammar}
              hidden={isGrammar}
            />
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!replyText.trim()) return toast.error('Escribe un texto o envía un audio');
                  await postComment({ body: replyText, parent_id: c.comment_id });
                  setReplyTo(null); setReplyText('');
                }}
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm font-semibold"
                data-testid={`reply-send-text-${c.comment_id}`}
              >
                <PaperPlaneTilt size={14} weight="fill" /> Enviar texto
              </button>
              <button onClick={() => { setReplyTo(null); setReplyText(''); }} className="bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded text-sm">Cancelar</button>
            </div>
          </div>
        ) : (
          <button onClick={() => { setReplyTo(c.comment_id); setReplyText(''); }} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline" data-testid={`reply-btn-${c.comment_id}`}>
            <Pencil size={12} /> Corregir
          </button>
        )
      )}
    </div>
  );

  return (
    <div className="mt-3 pt-3 border-t border-stone-200" data-testid={`comments-${materialId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#78716C] flex items-center gap-1">
          <ChatCircleDots size={14} weight="duotone" /> Respuestas ({studentMode ? 0 : items.length})
        </h4>
        {isTeacher && (
          <button
            onClick={() => setStudentMode(s => !s)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              studentMode
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'bg-white text-[#292524] border-stone-300 hover:bg-stone-50'
            }`}
            data-testid={`comments-student-mode-toggle-${materialId}`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${studentMode ? 'bg-white' : 'bg-emerald-500'}`} />
            {studentMode ? 'Vista de alumno activa' : 'Ver como alumno'}
          </button>
        )}
      </div>

      {/* In studentMode the teacher gets a clean preview: existing comments
          are hidden, only the composer remains, so the UI matches what a
          fresh student lands on. */}
      {!studentMode && loading && <p className="text-xs text-[#78716C]">Cargando…</p>}
      {!studentMode && !loading && roots.length === 0 && (
        <p className="text-xs text-[#A8A29E] italic mb-2">Sin respuestas todavía.</p>
      )}
      {!studentMode && (
        <div className="space-y-2">
          {roots.map(c => renderComment(c, { isCorrection: false }))}
        </div>
      )}
      {studentMode && (
        <p className="text-xs text-emerald-700 italic mb-2 font-semibold">
          Vista previa: así verá el alumno este apartado al entrar por primera vez (sin respuestas anteriores).
        </p>
      )}

      {canParticipate && !studentMode && (
        <div className="mt-3 space-y-2 bg-[#FAF9F6] rounded-lg p-2 border border-stone-200" data-testid={`comment-composer-${materialId}`}>
          {!studentOnSpeaking && (
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={2}
              placeholder={isGrammar
                ? '¿Tienes dudas? Escribe aquí lo que no entiendas de este tema y se enviará a Fragen → Grammatik.'
                : 'Escribe un mensaje…'}
              className="w-full border border-stone-300 rounded px-2 py-1 text-sm"
              data-testid={`composer-text-${materialId}`}
            />
          )}
          {studentOnSpeaking && (
            <p className="text-xs text-[#78716C] italic">
              <MicrophoneStage size={12} weight="fill" className="inline mr-1" />
              Antworte mit einer Sprachnachricht / Responde con un mensaje de voz.
            </p>
          )}
          {/* Audio + file replies are hidden on Grammatik — dudas are text-only. */}
          {!isGrammar && (
            <AudioRecorder
              compact
              testId={`composer-audio-${materialId}`}
              maxSeconds={300}
              onUploaded={async (f) => {
                await postComment({ body: studentOnSpeaking ? '' : text, audio_url: f.url });
                setText('');
              }}
            />
          )}
          <div className="flex gap-2 flex-wrap">
            {!studentOnSpeaking && (
              <button
                onClick={async () => {
                  if (!text.trim()) return toast.error(isGrammar ? 'Escribe tu duda' : 'Escribe un texto o envía un audio');
                  await postComment({ body: text });
                  setText('');
                  if (isGrammar) toast.success('Tu duda se envió a Fragen → Grammatik');
                }}
                className="inline-flex items-center gap-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white px-3 py-1.5 rounded text-sm font-semibold"
                data-testid={`composer-send-text-${materialId}`}
              >
                <PaperPlaneTilt size={14} weight="fill" />
                {isGrammar ? 'Enviar duda' : 'Enviar texto'}
              </button>
            )}
            {!isGrammar && !studentOnSpeaking && (
              <span className="text-[11px] text-[#78716C] self-center">
                <MicrophoneStage size={12} weight="fill" className="inline" /> o graba un audio
              </span>
            )}
          </div>
        </div>
      )}
      {/* Preview-only composer for the teacher's studentMode: visually
          identical layout, but inputs are disabled and the submit button
          is a stub. Lets the teacher confirm what their students see. */}
      {studentMode && canParticipate && (
        <div className="mt-3 space-y-2 bg-[#FAF9F6] rounded-lg p-2 border border-emerald-200" data-testid={`comment-composer-preview-${materialId}`}>
          {!studentOnSpeaking && (
            <textarea
              disabled
              rows={2}
              placeholder={isGrammar
                ? '¿Tienes dudas? Escribe aquí lo que no entiendas de este tema y se enviará a Fragen → Grammatik.'
                : 'Escribe un mensaje…'}
              className="w-full border border-stone-300 rounded px-2 py-1 text-sm bg-white"
            />
          )}
          {studentOnSpeaking && (
            <p className="text-xs text-[#78716C] italic">
              <MicrophoneStage size={12} weight="fill" className="inline mr-1" />
              Antworte mit einer Sprachnachricht / Responde con un mensaje de voz.
            </p>
          )}
          {!isGrammar && (
            <div className="text-[11px] text-emerald-700 font-semibold italic">
              <MicrophoneStage size={12} weight="fill" className="inline mr-1" />
              Aquí aparecerá el grabador de audio para el alumno.
            </div>
          )}
          <div className="flex gap-2 flex-wrap items-center">
            {!studentOnSpeaking && (
              <button
                disabled
                className="inline-flex items-center gap-1 bg-stone-300 text-white px-3 py-1.5 rounded text-sm font-semibold cursor-not-allowed"
              >
                <PaperPlaneTilt size={14} weight="fill" />
                {isGrammar ? 'Enviar duda' : 'Enviar texto'}
              </button>
            )}
            <span className="text-[11px] text-emerald-700 italic font-semibold">Modo previsualización — no se envía nada</span>
          </div>
        </div>
      )}
    </div>
  );
}
